import React, {useContext, useEffect, useState} from 'react';
import Sidebar from "../components/Sidebar";
import {Box, Grid} from '@mui/material';
import Typography from "@mui/material/Typography";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {csCZ as datePickersCsCZ} from "@mui/x-date-pickers/locales";
import {csCZ as dataGridCsCZ} from "@mui/x-data-grid/locales";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import CircularProgress from '@mui/material/CircularProgress';
import dayjs from "dayjs";
import {Link} from "react-router-dom";
import {GlobalDataContext} from "../components/GlobalDataProvider";
import {DataGridPro, gridClasses} from "@mui/x-data-grid-pro";
import {groupsAndActivitiesColumns, groupsAndActivitiesGroupingModel} from "../data/DatatableColumns";
import axios from "axios";
import {useAuthHeader} from "react-auth-kit";
import LoadingButton from "@mui/lab/LoadingButton";
import RefreshIcon from "@mui/icons-material/Refresh";


export default function Activities() {
  const {groupsAndActivitiesCompanies} = useContext(GlobalDataContext);
  const [businessMonthFrom, setBusinessMonthFrom] = useState("0124");
  const [businessMonthTo, setBusinessMonthTo] = useState("1224");
  const [isUpdating, setIsUpdating] = useState(true);
  const [updateMessage, setUpdateMessage] = useState("Načítám status...");
  const [months, setMonths] = useState([]);
  const [rows, setRows] = useState([]);
  const getAuthHeader = useAuthHeader();
  const getTreeDataPath = (row) => row.hierarchy;


  useEffect(() => {
    if (groupsAndActivitiesCompanies && groupsAndActivitiesCompanies.length > 0) {
      generateMonthArray(businessMonthFrom, businessMonthTo)
    }
  }, [businessMonthFrom, businessMonthTo, groupsAndActivitiesCompanies]);

  useEffect(() => {
    if (groupsAndActivitiesCompanies && groupsAndActivitiesCompanies.length > 0 && months && months.length > 0) {
      const rows = prepareRows(groupsAndActivitiesCompanies);
      setRows(rows)
    }
  }, [months]);

  const groupingColDef = {
    headerName: 'Firma',
    minWidth: 250,
    filterable: true,
    disableColumnMenu: false,
    hideDescendantCount: true,
    valueGetter: (value, row, column, apiRef) => {
      const rowId = apiRef.current.getRowId(row);
      const rowNode = apiRef.current.getRowNode(rowId);
      if (rowNode?.depth === 0) return rowNode?.groupingKey;
      if (rowNode?.depth === 1 && rowNode?.parent.includes("Celkem")) return rowNode?.parent;
      if (rowNode?.depth === 1) return rowNode?.groupingKey;
      if (rowNode?.depth === 2) return rowNode?.parent;
    },
    valueFormatter: (value) => {
      return value?.groupingKey
    },
  };
  const dataTypeTranslations = {
    incomes: "Příjmy",
    outcomes: "Výdaje",
    dph: "DPH",
    investments: "Investice",
    sale: "Prodej",
    rent: "Pronájem",
    loanBank: "Půjčka - banka",
    loanGroup: "Půjčka - skupina",
    "auto-generated-row-null/DEVELOPMENT-null/NOHO Tangata s.r.o.": "Příjmy",
  };

// Function to translate data types to Czech
  function translateDataType(dataType) {
    return dataTypeTranslations[dataType] || dataType;
  }

  const prepareRows = (data) => {
    const dataTypes = ["outcomes", "incomes", "dph", "investments", "loanBank", "loanGroup"]; // Sale and rent removed based on client request
    let rows = [];
    // Initialize an object to store overall sums for each assumption and reality column
    const overallSums = {};
    months.forEach(month => {
      overallSums[`assumptions_${month}`] = {};
      overallSums[`reality_${month}`] = {};
      dataTypes.forEach(type => {
        overallSums[`assumptions_${month}`][type] = 0;
        overallSums[`reality_${month}`][type] = 0;
      });
    });

    // Initialize sum objects for each data type
    const totalSums = {};
    dataTypes.forEach(type => {
      totalSums[type] = {
        sumAssumptions: 0,
        sumReality: 0,
        totalAssumptionValues: 0,
        totalValues: 0
      };
    });

    data.forEach(data => {
      dataTypes.forEach(dataType => {
        let row = {
          id: `${data.id}-${dataType}`,
          hierarchy: [data.group ? data.group : 'Prázdná skupina', data.name, translateDataType(dataType)]
        };

        // Initialize sum variables for each row
        let sumAssumptions = 0;
        let sumReality = 0;

        months.forEach(month => {
          const assumptionData = data.assumptionData[month] || {};
          const monthlyData = data.monthlyData[month] || {};

          const assumptionValue = assumptionData[dataType] || 0;
          const realityValue = monthlyData[dataType] || 0;

          row[`assumptions_${month}`] = assumptionValue;
          row[`reality_${month}`] = realityValue;

          // Update sum values for the row
          sumAssumptions += assumptionValue;
          sumReality += realityValue;

          // Update overall sums
          overallSums[`assumptions_${month}`][dataType] += assumptionValue;
          overallSums[`reality_${month}`][dataType] += realityValue;
        });

        // Assign sum values to the row
        row['sum_assumptions'] = sumAssumptions;
        row['sum_reality'] = sumReality;

        // Extract the total assumption and reality values from the group data
        row['total_assumption_values'] = data.totalAssumptionValues[dataType] || 0;
        row['total_values'] = data.totalValues[dataType] || 0;

        totalSums[dataType].sumAssumptions += sumAssumptions;
        totalSums[dataType].sumReality += sumReality;
        totalSums[dataType].totalAssumptionValues += row['total_assumption_values'];
        totalSums[dataType].totalValues += row['total_values'];

        rows.push(row);
      });
    });

    // Add final overall sum rows for each dataType
    dataTypes.forEach(dataType => {
      const sumRow = {id: `sum-${dataType}`, hierarchy: ["Celkem", translateDataType(dataType)]};
      let totalSumAssumptions = 0;
      let totalSumReality = 0;

      months.forEach(month => {
        sumRow[`assumptions_${month}`] = overallSums[`assumptions_${month}`][dataType];
        sumRow[`reality_${month}`] = overallSums[`reality_${month}`][dataType];
        totalSumAssumptions += overallSums[`assumptions_${month}`][dataType];
        totalSumReality += overallSums[`reality_${month}`][dataType];
      });

      sumRow['sum_assumptions'] = totalSumAssumptions;
      sumRow['sum_reality'] = totalSumReality;
      sumRow['total_assumption_values'] = totalSums[dataType].totalAssumptionValues;
      sumRow['total_values'] = totalSums[dataType].totalValues;

      rows.push(sumRow);
    });

    // Add a cashFlow row within the sum hierarchy
    const cashFlowRow = {id: 'cashFlow', hierarchy: ["Čistý finanční tok"]};
    let totalCashFlowAssumptions = 0;
    let totalCashFlowReality = 0;
    let totalCashFlowAssumptionValues;
    let totalCashFlowValues;

    months.forEach(month => {
      const incomesAssumptions = overallSums[`assumptions_${month}`]['incomes'] || 0;
      const outcomesAssumptions = overallSums[`assumptions_${month}`]['outcomes'] || 0;
      const incomesReality = overallSums[`reality_${month}`]['incomes'] || 0;
      const outcomesReality = overallSums[`reality_${month}`]['outcomes'] || 0;

      const cashFlowAssumptions = incomesAssumptions - outcomesAssumptions;
      const cashFlowReality = incomesReality - outcomesReality;

      cashFlowRow[`assumptions_${month}`] = cashFlowAssumptions;
      cashFlowRow[`reality_${month}`] = cashFlowReality;

      totalCashFlowAssumptions += cashFlowAssumptions;
      totalCashFlowReality += cashFlowReality;
    });

    // Calculate total cash flow assumption and reality values
    totalCashFlowAssumptionValues = totalSums['incomes'].totalAssumptionValues - totalSums['outcomes'].totalAssumptionValues;
    totalCashFlowValues = totalSums['incomes'].totalValues - totalSums['outcomes'].totalValues;

    // Assign totals to cashFlowRow
    cashFlowRow['sum_assumptions'] = totalCashFlowAssumptions;
    cashFlowRow['sum_reality'] = totalCashFlowReality;
    cashFlowRow['total_assumption_values'] = totalCashFlowAssumptionValues;
    cashFlowRow['total_values'] = totalCashFlowValues;

    rows.push(cashFlowRow);

    // Sort the rows based on the custom logic: valid group names first, then 'Prázdná skupina', and then 'Celkem' and 'Cashflow'
    rows.sort((a, b) => {
      const aHierarchy = a.hierarchy[0];
      const bHierarchy = b.hierarchy[0];

      // Sort 'Celkem' and 'Čistý finanční tok' at the end
      if (aHierarchy === 'Celkem' || aHierarchy === 'Čistý finanční tok') return 1;
      if (bHierarchy === 'Celkem' || bHierarchy === 'Čistý finanční tok') return -1;

      // Sort 'Prázdná skupina' after named groups
      if (aHierarchy === 'Prázdná skupina') return 1;
      if (bHierarchy === 'Prázdná skupina') return -1;

      // Otherwise, sort alphabetically by the group name
      return aHierarchy.localeCompare(bHierarchy);
    });

    console.log("ActivitiesRows", rows);

    return rows;
  };

  function convertDateToMMYYDayjs(date) {
    return dayjs(date).format('MMYY').toString();
  }

  const handleDateFromChange = (newValue) => {
    setBusinessMonthFrom(convertDateToMMYYDayjs(newValue.toString()));
  };
  const handleDateToChange = (newValue) => {
    setBusinessMonthTo(convertDateToMMYYDayjs(newValue.toString()));
  };

  function convertStringToDate(dateString) {
    if (!dateString || dateString === '') {
      return;
    }
    if (dateString.length !== 4) {
      return;
    }
    const month = parseInt(dateString.substring(0, 2), 10);
    const year = parseInt(`20${dateString.substring(2, 4)}`, 10);

    return dayjs(new Date(year, month - 1, 1));
  }

  function generateMonthArray(from, to) {
    if (!from || !to) {
      return [];
    }
    const fromMonth = parseInt(from.substring(0, 2), 10);
    const fromYear = parseInt(`20${from.substring(2, 4)}`, 10);
    const toMonth = parseInt(to.substring(0, 2), 10);
    const toYear = parseInt(`20${to.substring(2, 4)}`, 10);

    let startDate = dayjs(new Date(fromYear, fromMonth - 1, 1));
    const endDate = dayjs(new Date(toYear, toMonth - 1, 1));

    const months = [];

    while (startDate.isBefore(endDate) || startDate.isSame(endDate, 'month')) {
      const month = startDate.format('MMYY');
      months.push(month);
      startDate = startDate.add(1, 'month');
    }

    setMonths(months);
  }

  useEffect(() => {
    getUpdatedDataTime();
  }, []);

  const handleDataRefresh = async () => {
    const token = getAuthHeader();
    try {
      await axios.get(`https://flexioverview.upi-group.cz/dataset/update`, {
        headers: { Authorization: token },
      });
      setIsUpdating(true);
      getUpdatedDataTime();
    } catch (error) {
      setUpdateMessage("Při aktualizaci dat došlo k chybě. Zkuste to znovu...");
      console.error(error);
    }
  };

  const getUpdatedDataTime = async () => {
    const token = getAuthHeader();

    const response = await axios.get(`https://flexioverview.upi-group.cz/dataset/get`, {
      headers: { Authorization: token },
    });

    if (response.data.updating === false) {
      setUpdateMessage(response.data.message);
      setIsUpdating(false)
      return;
    }

    setUpdateMessage("Aktualizuji data...");


    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`https://flexioverview.upi-group.cz/dataset/get`, {
          headers: { Authorization: token },
        });

        if (response.data.updating === false) {
          setUpdateMessage(response.data.message);
          setIsUpdating(false);
          clearInterval(interval);
        }
      } catch (error) {
        setIsUpdating(false);
        setUpdateMessage("Při aktualizaci dat došlo k chybě. Zkuste to znovu...");
        clearInterval(interval);
      }
    }, 2000);
  };

  return (
    <Sidebar>
      <Grid container justifyContent="start">
        <Grid item xs={6}>
      <Typography variant="h5" fontWeight={600}>Data podle činností</Typography>
        </Grid>
        <Grid item xs={6} textAlign="right" sx={{height: '200%'}}>
          <Typography variant="text">
            {updateMessage}
          </Typography>
        </Grid>
        <Grid item xs={6}>
      <Typography variant="h7" fontWeight={400}>
        <Link to="/home" style={{textDecoration: 'none', color: 'black'}}>NOHO</Link> -> Činnosti
      </Typography>
        </Grid>
          <Grid item xs={6} textAlign="right">
            <LoadingButton
              size="small"
              onClick={handleDataRefresh}
              endIcon={<RefreshIcon />}
              loading={isUpdating}
              loadingPosition="end"
              variant="contained"
            >
              Aktualizovat
            </LoadingButton>
          </Grid>
        </Grid>
      <Box sx={{width: "100%", marginTop: "10px", backgroundColor: '#f5f5f5', padding: 2, marginBottom: 2}}>
        <LocalizationProvider adapterLocale="cs" dateAdapter={AdapterDayjs}
                              localeText={datePickersCsCZ.components.MuiLocalizationProvider.defaultProps.localeText}>
          <DatePicker
            label="Od"
            views={['year', 'month']}
            value={convertStringToDate(businessMonthFrom)}
            onChange={handleDateFromChange}
            sx={{marginRight: "15px", backgroundColor: "white", borderRadius: "4px"}}
          />
          <DatePicker
            label="Do"
            views={['year', 'month']}
            value={convertStringToDate(businessMonthTo)}
            onChange={handleDateToChange}
            sx={{marginRight: "15px", backgroundColor: "white", borderRadius: "4px"}}
          />
        </LocalizationProvider>
      </Box>
      {
        groupsAndActivitiesCompanies && groupsAndActivitiesCompanies.length > 0 && months.length > 0 && rows.length > 0 ? (
          <div style={{height: "78vh", width: '100%'}}>
            <DataGridPro
              treeData
              rows={rows}
              columns={groupsAndActivitiesColumns(months)}
              disableColumnReorder
              initialState={{
                ...rows.initialState,
                pinnedColumns: {
                  left: ["__tree_data_group__"],
                  right: ['sum_assumptions', 'sum_reality', 'total_assumption_values', 'total_values'],
                },
              }}
              getTreeDataPath={getTreeDataPath}
              hideFooter
              localeText={dataGridCsCZ.components.MuiDataGrid.defaultProps.localeText}
              groupingColDef={groupingColDef}
              columnGroupingModel={groupsAndActivitiesGroupingModel(months)}
              getCellClassName={(params) => {
                let classNames = '';

                if (params.value < 0) {
                  classNames += 'negative ';
                }

                if (params.colDef.headerName.includes("Skutečnost") && (params.rowNode.groupingKey.includes("Celkem") || params.rowNode.parent.includes("Celkem"))) {
                  classNames += 'spendings-total ';
                }
                if (params.colDef.headerName.includes("Skutečnost") && params.id.includes("cashFlow")) {
                  classNames += 'spendings-cashflow ';
                }
                if (params.colDef.headerName.includes("Skutečnost")) {
                  classNames += 'spendings ';
                }
                if (params.rowNode.groupingKey.includes("Celkem") || params.rowNode.parent.includes("Celkem")) {
                  classNames += 'total ';
                }
                if (params.id.includes("cashFlow")) {
                  classNames += 'cashflow ';
                }

                return classNames.trim();
              }}
              sx={{
                '&.MuiDataGrid-root': {
                  borderRadius: '0px',
                  borderLeft: 'none',
                  borderRight: 'none',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 'bold', overflow: 'visible',
                },
                '& .spendings': {
                  borderRight: "1px solid rgba(224, 224, 224, 1)",
                },
                '& .total': {
                  fontWeight: 'bold',
                },
                '& .cashflow': {
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                },
                '& .spendings-cashflow': {
                  fontWeight: 'bold',
                  borderRight: "1px solid rgba(224, 224, 224, 1)",
                  backgroundColor: '#f5f5f5',
                },
                '& .spendings-total': {
                  borderRight: "1px solid rgba(224, 224, 224, 1)",
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-cell.negative': {
                  color: 'red',
                },
                [`.${gridClasses.cell}.pinned-row`]: {
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                },
                [`.${gridClasses.cell}.pinned-row_spendings`]: {
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  borderRight: "1px solid rgba(224, 224, 224, 1)",
                },
                [`.${gridClasses.cell}.spendings`]: {
                  borderRight: "1px solid rgba(224, 224, 224, 1)",
                },
              }}
            />

          </div>

        ) : (
          <CircularProgress sx={{position: 'absolute', top: '50%', left: '50%'}}/>
        )
      }
    </Sidebar>
  );
}
